import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useRef,
} from 'react';
import { io } from 'socket.io-client';
import { useAuth } from './AuthContext';
import notificationManager from '../utils/notificationManager';
import {
  playOrderNotificationSound,
  playGeneralNotificationSound,
} from '../utils/notificationSound';

const SocketContext = createContext();

export const useSocket = () => {
  const context = useContext(SocketContext);
  if (!context) {
    throw new Error('useSocket must be used within a SocketProvider');
  }
  return context;
};

export const SocketProvider = ({ children }) => {
  const { vendor, isAuthenticated } = useAuth();
  const [socket, setSocket] = useState(null);
  const [isConnected, setIsConnected] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [notificationPermission, setNotificationPermission] =
    useState('default');
  const reconnectTimeoutRef = useRef(null);
  const pingIntervalRef = useRef(null);
  const audioRef = useRef(null);
  const processedNotificationsRef = useRef(new Set()); // Track processed notifications

  // Request notification permission on mount
  useEffect(() => {
    if ('Notification' in window) {
      setNotificationPermission(Notification.permission);

      if (Notification.permission === 'default') {
        Notification.requestPermission().then((permission) => {
          setNotificationPermission(permission);
        });
      }
    }
  }, []);

  useEffect(() => {
    const unlockAudio = () => {
      try {
        const AudioCtx = window.AudioContext || window.webkitAudioContext;
        if (!AudioCtx) return;
        const ctx = new AudioCtx();
        if (ctx.state === 'suspended') {
          ctx.resume();
        }
        window.removeEventListener('click', unlockAudio);
        console.log('🔊 Audio unlocked');
      } catch (err) {
        console.log('Audio unlock failed:', err);
      }
    };

    window.addEventListener('click', unlockAudio);
    return () => window.removeEventListener('click', unlockAudio);
  }, []);

  // Function to play notification sound based on type
  const playNotificationSound = (notificationType = 'general') => {
    // Check if sound notifications are enabled in settings
    const notificationSettings = JSON.parse(
      localStorage.getItem('notificationSettings') || '{"sound": true}'
    );

    if (!notificationSettings.sound) {
      return;
    }

    try {
      // Use different sounds for different notification types
      if (
        notificationType === 'order' ||
        notificationType === 'new-order' ||
        notificationType === 'order-status-update' ||
        notificationType === 'order-status-notification'
      ) {
        // Use MP3 file for order-related notifications
        playOrderNotificationSound();
      } else {
        // Use general notification sound for other notifications
        playGeneralNotificationSound();
      }
    } catch (error) {
      console.log('Error playing notification sound:', error);
    }
  };

  // Function to show browser notification
  const showBrowserNotification = (title, options = {}) => {
    // Check if browser notifications are enabled in settings
    const notificationSettings = JSON.parse(
      localStorage.getItem('notificationSettings') || '{"browser": true}'
    );

    if (
      'Notification' in window &&
      Notification.permission === 'granted' &&
      notificationSettings.browser
    ) {
      const notification = new Notification(title, {
        icon: '/favicon.ico', // Add your app icon
        badge: '/favicon.ico',
        tag: 'food-park-notification',
        requireInteraction: false,
        silent: false, // This will use system sound
        ...options,
      });

      // Auto close after 5 seconds (only for non-interactive notifications)
      if (!options.requireInteraction) {
        setTimeout(() => {
          notification.close();
        }, 5000);
      }

      // Handle click on notification
      notification.onclick = () => {
        window.focus();

        // Navigate based on notification type
        if (title.includes('New Order')) {
          // Extract order ID from title and navigate to specific order
          const orderIdMatch = title.match(/Order #(\d+)/);
          if (orderIdMatch) {
            const orderId = orderIdMatch[1];
            window.location.href = `/orders?order=${orderId}&action=view`;
          } else {
            window.location.href = '/orders';
          }
        } else if (title.includes('Status Updated')) {
          // Extract order ID from title and navigate to specific order
          const orderIdMatch = title.match(/Order #(\d+)/);
          if (orderIdMatch) {
            const orderId = orderIdMatch[1];
            window.location.href = `/orders?order=${orderId}&action=status`;
          } else {
            window.location.href = '/orders';
          }
        } else if (title.includes('Vendor Notification')) {
          // Navigate to dashboard for general vendor notifications
          window.location.href = '/dashboard';
        }

        notification.close();
      };

      return notification;
    }
  };

  useEffect(() => {
    if (!isAuthenticated || !vendor) {
      return;
    }

    // Initialize socket connection
    const socketUrl =
      process.env.REACT_APP_SOCKET_URL ||
      (process.env.NODE_ENV === 'production'
        ? 'http://52.56.190.24'
        : 'http://localhost:3000');

    const newSocket = io(socketUrl, {
      transports: ['websocket', 'polling'],
      autoConnect: true,
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      maxReconnectionAttempts: 5,
    });

    // Connection events
    const handleConnect = () => {
      console.log('🔌 Connected to WebSocket server');
      setIsConnected(true);

      // Authenticate vendor
      newSocket.emit('vendor-auth', vendor.id);

      // Join vendor room
      newSocket.emit('join-vendor-room', vendor.id);

      // Clear any existing reconnect timeout
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }

      // Start ping interval
      pingIntervalRef.current = setInterval(() => {
        newSocket.emit('ping');
      }, 30000); // Ping every 30 seconds
    };

    const handleDisconnect = () => {
      console.log('🔌 Disconnected from WebSocket server');
      setIsConnected(false);

      // Clear ping interval
      if (pingIntervalRef.current) {
        clearInterval(pingIntervalRef.current);
        pingIntervalRef.current = null;
      }
    };

    const handleConnectError = (error) => {
      console.error('🔌 WebSocket connection error:', error);
      setIsConnected(false);
    };

    // Authentication events
    const handleAuthSuccess = (data) => {
      console.log('🔐 Vendor authenticated successfully:', data);
    };

    const handleAuthError = (error) => {
      console.error('🔐 Vendor authentication error:', error);
    };

    // Room events
    const handleRoomJoined = (data) => {
      console.log('🏠 Joined vendor room:', data);
    };

    // Enhanced notification events with deduplication
    const handleOrderNotification = (notification) => {
      // Create unique key for deduplication
      const notificationKey =
        notification.type === 'new-order'
          ? `order-${notification.order?.id}-${notification.type}`
          : `order-${notification.orderId || notification.order?.id}-${
              notification.type
            }-${notification.status}`;

      // Check if we've already processed this notification (within last 5 seconds)
      if (processedNotificationsRef.current.has(notificationKey)) {
        console.log('⚠️ Duplicate notification ignored:', notificationKey);
        return;
      }

      // Mark as processed
      processedNotificationsRef.current.add(notificationKey);

      // Remove from processed set after 5 seconds to allow legitimate duplicates
      setTimeout(() => {
        processedNotificationsRef.current.delete(notificationKey);
      }, 5000);

      console.log('📦 Received order notification:', notification);

      // Add notification to state
      setNotifications((prev) => {
        // Check if notification already exists in state
        const exists = prev.some(
          (n) =>
            (n.type === 'new-order' &&
              n.order?.id === notification.order?.id) ||
            (n.type === notification.type &&
              n.orderId === notification.orderId &&
              n.status === notification.status)
        );
        if (exists) {
          return prev; // Don't add duplicate
        }
        return [notification, ...prev.slice(0, 9)]; // Keep last 10 notifications
      });

      // Play notification sound with order type
      playNotificationSound(notification.type);

      // Show toast notification using notification manager
      if (notification.type === 'new-order') {
        notificationManager.showNewOrderNotification(notification.order);

        // Browser notification for new orders
        showBrowserNotification(`New Order #${notification.order.id}`, {
          body: `£${notification.order.total} • ${
            notification.order.orderItems?.length || 0
          } items • ${notification.order.order_type}`,
          icon: '/favicon.ico',
          badge: '/favicon.ico',
          tag: `new-order-${notification.order.id}`,
          requireInteraction: true, // Require interaction for new orders
        });
      } else if (
        notification.type === 'order-status-update' ||
        notification.type === 'order-status-notification'
      ) {
        notificationManager.showStatusUpdateNotification(
          notification.orderId,
          notification.status,
          notification.order
        );

        // Browser notification for status updates
        showBrowserNotification(
          `Order #${notification.orderId} Status Updated`,
          {
            body: `Status changed to: ${notification.status.replace('_', ' ')}`,
            icon: '/favicon.ico',
            tag: `status-update-${notification.orderId}`,
            requireInteraction: false,
          }
        );
      }
    };

    const handleVendorNotification = (notification) => {
      // Create unique key for deduplication
      const notificationKey = `vendor-${notification.id || Date.now()}-${
        notification.message
      }`;

      if (processedNotificationsRef.current.has(notificationKey)) {
        console.log(
          '⚠️ Duplicate vendor notification ignored:',
          notificationKey
        );
        return;
      }

      processedNotificationsRef.current.add(notificationKey);
      setTimeout(() => {
        processedNotificationsRef.current.delete(notificationKey);
      }, 5000);

      console.log('📢 Received vendor notification:', notification);

      // Add notification to state
      setNotifications((prev) => {
        const exists = prev.some((n) => n.id === notification.id);
        if (exists) {
          return prev;
        }
        return [notification, ...prev.slice(0, 9)];
      });

      // Play notification sound with vendor notification type
      playNotificationSound('vendor-notification');

      // Show toast notification using notification manager
      notificationManager.showVendorNotification(
        notification.message,
        notification
      );

      // Browser notification
      showBrowserNotification('Vendor Notification', {
        body: notification.message,
        icon: '/favicon.ico',
        tag: 'vendor-notification',
      });
    };

    // Ping/pong for connection health
    const handlePong = () => {
      // Connection is healthy
    };

    // Register all event listeners
    newSocket.on('connect', handleConnect);
    newSocket.on('disconnect', handleDisconnect);
    newSocket.on('connect_error', handleConnectError);
    newSocket.on('auth-success', handleAuthSuccess);
    newSocket.on('auth-error', handleAuthError);
    newSocket.on('room-joined', handleRoomJoined);
    newSocket.on('order-notification', handleOrderNotification);
    newSocket.on('vendor-notification', handleVendorNotification);
    newSocket.on('pong', handlePong);

    setSocket(newSocket);

    // Cleanup function - IMPORTANT: Remove all event listeners
    return () => {
      // Remove all event listeners to prevent duplicates
      newSocket.off('connect', handleConnect);
      newSocket.off('disconnect', handleDisconnect);
      newSocket.off('connect_error', handleConnectError);
      newSocket.off('auth-success', handleAuthSuccess);
      newSocket.off('auth-error', handleAuthError);
      newSocket.off('room-joined', handleRoomJoined);
      newSocket.off('order-notification', handleOrderNotification);
      newSocket.off('vendor-notification', handleVendorNotification);
      newSocket.off('pong', handlePong);

      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
      if (pingIntervalRef.current) {
        clearInterval(pingIntervalRef.current);
      }
      newSocket.disconnect();
    };
  }, [isAuthenticated, vendor]);

  // Clear notifications
  const clearNotifications = () => {
    setNotifications([]);
  };

  // Remove specific notification
  const removeNotification = (index) => {
    setNotifications((prev) => prev.filter((_, i) => i !== index));
  };

  // Request notification permission
  const requestNotificationPermission = async () => {
    if ('Notification' in window) {
      const permission = await Notification.requestPermission();
      setNotificationPermission(permission);
      return permission;
    }
    return 'denied';
  };

  const value = {
    socket,
    isConnected,
    notifications,
    notificationPermission,
    clearNotifications,
    removeNotification,
    requestNotificationPermission,
  };

  return (
    <SocketContext.Provider value={value}>
      {/* Hidden audio element for notification sounds */}
      <audio ref={audioRef} preload="auto" style={{ display: 'none' }}>
        <source src="/notification-sound.mp3" type="audio/mpeg" />
        <source src="/notification-sound-1.mp3" type="audio/mpeg" />
      </audio>
      {children}
    </SocketContext.Provider>
  );
};
